import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Snackbar,
  Alert,
  Button,
  Box,
} from "@mui/material";
import { useHistory, useParams } from "react-router-dom";
import { useQuery, gql } from "@apollo/client";
import autoGradeClient from "../../../../utils/graphql/autoGradeClient";
import { GET_ALL_EXAM_PAPERS_SUBJECTS } from "utils/graphql/services/ai-evaluation/queries";

function ViewExamPaperSubjectPage() {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "error" | "info";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const { data, loading, error } = useQuery(GET_ALL_EXAM_PAPERS_SUBJECTS, {
    variables: {
      input: {
        examPaperId: parseInt(id),
      },
    },
    client: autoGradeClient,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (error) {
      setSnackbar({
        open: true,
        message: "API Error: " + error.message,
        severity: "error",
      });
    } else if (data?.getExamPaperSubjects?.examPaperSubjects?.length === 0) {
      setSnackbar({
        open: true,
        message: "No subjects found for this exam paper.",
        severity: "info",
      });
    }
  }, [data, error]);

  const handleCloseSnackbar = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  if (loading) return <Typography>Loading...</Typography>;

  const rows = data?.getExamPaperSubjects?.examPaperSubjects || [];
  const totalRecords = data?.getExamPaperSubjects?.totalRecords || 0;

  return (
    <>
      <Grid container spacing={3} sx={{ p: 2 }}>
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Typography variant="h6">Exam Paper Subjects #{id}</Typography>
              <Button variant="outlined" onClick={() => history.goBack()}>
                Close
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Exam Paper ID</TableCell>
                    <TableCell>Subject ID</TableCell>
                    <TableCell>Archived</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Updated By</TableCell>
                    <TableCell>Updated At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row: any) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.examPaperId}</TableCell>
                        <TableCell>{row.subjectId}</TableCell>
                        <TableCell>{row.isArchived ? "Yes" : "No"}</TableCell>
                        <TableCell>{row.createdByName || "-"}</TableCell>
                        <TableCell>
                          {new Date(row.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell>{row.updatedByName || "-"}</TableCell>
                        <TableCell>
                          {new Date(row.updatedAt).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </Paper>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ViewExamPaperSubjectPage;
